/**
 * StoreSelect — F3 (DISC-127/130 闭合) Antd Select 门店下拉, showSearch + filter.
 *
 * 把 v0.10 PR3 frontend 二轮的"用户手填 store_id" InputNumber 升级为下拉.
 *
 * 设计:
 * - 单店 mode (默认 multiple=false): 选 1 个 store_id, 与原 `<InputNumber>` 单值语义对齐
 * - 多店 mode (multiple=true): 选 N 个 store_id (Array<number>), 与原 `mode='tags'` 多选语义对齐
 * - showSearch: 默认开 (filterStoreOption: label + internal_code 大小写不敏感)
 * - excludeStoreIds: 调用方传入排除集 (e.g. AreaManagerStoresSection 新增门店时排除已绑店)
 * - 加载态: loading prop 显式 spinner; 错误态 + 空态走 `notFoundContent`
 * - 共享 cache: 多个 StoreSelect mount 同时只触发 1 次 backend GET (useStoresOptions cache)
 *
 * 不接管:
 * - Form.Item 包装 / 校验规则 / required: 由调用方 Form.Item 控制
 * - includeInactive 切换: 默认 false (普通调用方不需下线店); area_manager 创建 / 详情区
 *   均不应见下线店. priv-role 后续如有"补建已下线归属"等场景再扩.
 */

import { Select, type SelectProps } from 'antd';
import { useMemo, type FC } from 'react';
import {
  useStoresOptions,
  filterStoreOption,
  type StoreOption,
} from '../hooks/useStoresOptions';

export interface StoreSelectProps {
  /** 单店选: number | undefined; 多店选: number[]. */
  value?: number | number[];
  onChange?: (value: number | number[]) => void;
  /** 默认 false; true=Array<number>, 走 antd `mode="multiple"`. */
  multiple?: boolean;
  /** 排除集 (e.g. 已绑门店不可再选). 仅在 options 派生时滤掉, 不影响 cache. */
  excludeStoreIds?: number[];
  /** 默认 "搜门店..." */
  placeholder?: string;
  disabled?: boolean;
  /** 透传 (test-id / autoFocus 等). */
  className?: string;
  'data-testid'?: string;
  style?: React.CSSProperties;
  /** 是否允许清空 (单选模式下 antd 默认 allowClear=false). */
  allowClear?: boolean;
  /** false = 下拉菜单按内容自适应宽度 (长店名不截断); 默认跟随输入框宽度. */
  popupMatchSelectWidth?: boolean;
}

export const StoreSelect: FC<StoreSelectProps> = ({
  value,
  onChange,
  multiple = false,
  excludeStoreIds,
  placeholder = '搜门店...',
  disabled,
  className,
  'data-testid': testId,
  style,
  allowClear,
  popupMatchSelectWidth,
}) => {
  const { options, loading, error } = useStoresOptions();

  const filteredOptions = useMemo<StoreOption[]>(() => {
    if (!excludeStoreIds || excludeStoreIds.length === 0) {
      return options;
    }
    const excludeSet = new Set(excludeStoreIds);
    return options.filter((o) => !excludeSet.has(o.value));
  }, [options, excludeStoreIds]);

  const notFoundContent = loading
    ? '加载中...'
    : error
      ? `加载失败: ${error}`
      : filteredOptions.length === 0
        ? options.length === 0
          ? '暂无可见门店'
          : '无可选门店 (已全部排除)'
        : null;

  // antd Select props 分单/多选两套 (类型 union). data-testid 直接挂在可点击
  // Select 根节点，避免 display:contents 包装让运行态测试只能退回 Ant 私有结构。
  const sharedProps: SelectProps = {
    showSearch: true,
    placeholder,
    loading,
    disabled,
    style: style ?? { width: '100%' },
    className,
    options: filteredOptions,
    notFoundContent,
    optionFilterProp: 'label',
    filterOption: (input, option) =>
      filterStoreOption(input, option as { label?: string; raw?: StoreOption['raw'] }),
    ...(popupMatchSelectWidth === undefined ? {} : { popupMatchSelectWidth }),
    ...(testId ? { 'data-testid': testId } : {}),
  };

  const inner = multiple ? (
    <Select
      {...sharedProps}
      mode="multiple"
      value={value as number[] | undefined}
      onChange={(v: number[]) => onChange?.(v)}
      allowClear={allowClear ?? true}
    />
  ) : (
    <Select
      {...sharedProps}
      value={value as number | undefined}
      onChange={(v: number) => onChange?.(v)}
      allowClear={allowClear ?? false}
    />
  );

  return inner;
};

export default StoreSelect;
